import Service, { service } from '@ember/service'
import { type UIScope } from 'district-ui-client/domain/ui-scope'
import type DateFilterService from 'district-ui-client/services/date-filter'
import type RouterService from '@ember/routing/router-service'
import type ActiveRouteService from 'district-ui-client/services/active-route'
import { type ReportPath } from 'district-ui-client/services/report-registry'
import type SessionService from 'district-ui-client/services/session'
import { assert } from '@ember/debug'

export default class ReportNavigatorService extends Service {
  @service protected dateFilter!: DateFilterService

  @service protected router!: RouterService

  @service protected activeRoute!: ActiveRouteService

  @service protected session!: SessionService

  get uiScope() {
    return this.activeRoute.reportingUiScope
  }

  get districtId() {
    return this.session.currentDistrict.id
  }

  goToReport(reportPath: ReportPath, { resetFilters }: { resetFilters: boolean } = { resetFilters: false }) {
    // always reset page, band etc.
    const queryParamsToReset = { pageNumber: 1, band: 'all' }
    if (resetFilters) {
      // clear scope & filters
      void this.router.transitionTo(reportPath, 'district', this.districtId, {
        queryParams: {
          ...queryParamsToReset,
          dateRange: undefined,
          studentGrade: undefined,
          contentLevel: undefined,
          schoolIds: undefined,
          lessonLevelDomainIds: undefined,
        },
      })
    } else {
      assert('uiScope is defined', this.uiScope)

      // keep scope & filters
      void this.router.transitionTo(reportPath, this.uiScope.scope, this.uiScope.id, {
        queryParams: { ...queryParamsToReset },
      })
    }
  }

  updateScope(uiScope: UIScope) {
    let queryParams = this.queryParamsToReset()

    if (uiScope.subScopes.length > 0) {
      queryParams = { ...queryParams, ...{ schoolIds: uiScope.subScopes.map((school) => school.id) } }
    } else {
      // if no subScopes we can remove schoolIds
      queryParams = { ...queryParams, ...{ schoolIds: undefined } }
    }

    void this.router.replaceWith(this.activeRoute.reportingPath, uiScope.scope, uiScope.id, {
      queryParams,
    })
  }

  updateQueryParams(queryParams: Record<string, unknown>) {
    const queryParamsToReset = this.queryParamsToReset()

    void this.router.replaceWith(this.activeRoute.reportingPath, {
      queryParams: { ...queryParamsToReset, ...queryParams },
    })
  }

  /**
   * Always reset page, band etc. need to explicitly give same dateRange else it sometimes resets to default when
   * moving to new scope / dynamic segments for some reason.
   */
  private queryParamsToReset() {
    return {
      pageNumber: 1,
      band: 'all',
      dateRange: this.dateFilter.toDateRange(this.dateFilter.period),
    }
  }
}

declare module '@ember/service' {
  interface Registry {
    'report-navigator': ReportNavigatorService
  }
}
